import React, { useCallback, useEffect, useState, useRef } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { FaCartPlus } from 'react-icons/fa';
import { FiX, FiStar, FiCheckCircle, FiXCircle } from 'react-icons/fi';
import Breadcrumb from '../Breadcrumb';
import { useGlobal } from '../../global/GlobalContext';
import ProductDetailsAccordian from '../ProductDetailsAccordian';
import YouAlsoLike from '../YouAlsoLike';
import Slider from 'react-slick';
import DeliveryEstimate from '../DeliveryEstimate';
import Zoom from "react-medium-image-zoom";
import Skeleton from 'react-loading-skeleton';
import { LazyLoadImage } from "react-lazy-load-image-component";
import ReviewRatingComments from '../ReviewRatingComments';
import GmbReviewsSection from '../GmbReviewsSection';
import axios from 'axios';
import RecentlyViewed from '../RecentlyViewed';
import { FaBolt } from 'react-icons/fa6';
import WishlistButton from '../WishlistButton';
import confetti from 'canvas-confetti';
import MetaData from '../../layout/MetaData';
import compressImage from '../../utils/compressImage';
import MetaContentConfig from '../../content/MetaContentConfig';
import { cacheManager } from '../../utils/cacheManager';
import { UserRole } from '../../config/userRole';

function ProductDetailsAll() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sizeChart, setSizeChart] = useState(false);
  const [keyHighlights, setKeyHighlights] = useState([]);
  const { wishlist, setWishlist, addToCart, token } = useGlobal();
  const [selectedQty, setSelectedQty] = useState(null);
  const [totalPrice, setTotalPrice] = useState(0);

  const [productVariations, setProductVariations] = useState([]);
  const [colorVariations, setColorVariations] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedSize, setSelectedSize] = useState(null);
  const [productReviews, setProductReviews] = useState([]);
  const [gmbReviews, setGmbReviews] = useState([]);
  const [addSuccess, setAddSuccess] = useState(false);
  const [addedProductName, setAddedProductName] = useState("");
  const [sizeError, setSizeError] = useState(false);
  const [addToCartLoading, setAddToCartLoading] = useState(false);
  const [buyNowLoading, setBuyNowLoading] = useState(false);
  const qtyRef = useRef(null);

  const navigate = useNavigate();

  const fetchProductDetails = useCallback(async () => {
    if (!token) {
      console.warn("Token missing. Aborting fetch.");
      return;
    }

    // Get the array of previously viewed products
    const viewedProducts = JSON.parse(cacheManager.get('fp_recentlyViewedProducts')) || [];

    // Check if the product is already in the list
    const existingProduct = viewedProducts.find((item) => item.slug === slug);
    if (existingProduct) {
      // Load from sessionStorage instead of calling the API
      setProduct(existingProduct);
      setSelectedImage(existingProduct.primary_img);
      setKeyHighlights(existingProduct.product_key_highlights || []);
      setColorVariations(existingProduct.color_details || []);
      setProductVariations(existingProduct.product_variations || []);
      setProductReviews(existingProduct.product_ratings || []);
      setSelectedQty(existingProduct.pack_qty);
      setTotalPrice(existingProduct.pack_price);
      setLoading(false);
      return;
    }

    // If not in sessionStorage, fetch from API
    setLoading(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/v1/get_slug_data/${slug}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = response.data;
      if (data.status) {
        const selectedProduct = data.product_details;
        const variations = selectedProduct?.product_variations || [];
        const variationsColor = selectedProduct?.color_details || [];

        setColorVariations(variationsColor);
        setProductVariations(variations);
        setProductReviews(selectedProduct?.product_ratings);
        setKeyHighlights(selectedProduct.product_key_highlights);
        setProduct(selectedProduct);
        setSelectedImage(selectedProduct.primary_img);
        setSelectedQty(selectedProduct.pack_qty);
        setTotalPrice(selectedProduct.pack_price);

        // Add to sessionStorage list
        const newViewedProducts = [...viewedProducts, selectedProduct];
        cacheManager.set('fp_recentlyViewedProducts', JSON.stringify(newViewedProducts));
      }
    } catch (error) {
      console.error('Error fetching product data:', error);
    } finally {
      setLoading(false);
    }
  }, [slug, token]);


  useEffect(() => {
    fetchProductDetails();
  }, [fetchProductDetails]);

  useEffect(() => {
    axios.get(`${process.env.REACT_APP_API_URL}/api/v1/get_gmb_reviews`)
      .then((res) => setGmbReviews(Array.isArray(res.data?.reviews) ? res.data.reviews : []))
      .catch(() => setGmbReviews([]));
  }, []);

  const calculateRatingBreakdown = (reviews) => {
    const breakdown = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
    const totalReviews = reviews.length;
    if (totalReviews === 0) return breakdown;
    // Count occurrences of each rating
    reviews.forEach(({ rating }) => {
      breakdown[rating] = (breakdown[rating] || 0) + 1;
    });
    return breakdown;
  };

  const ratingBreakdown = calculateRatingBreakdown(productReviews);

  const overallRating = productReviews.length > 0
    ? (productReviews.reduce((sum, item) => sum + item.rating, 0) / productReviews.length).toFixed(1)
    : 0;

  const getDefaultVariationId = useCallback(() => {
    if (!Array.isArray(productVariations) || productVariations.length === 0) return null;

    if (UserRole.userRole === "distributor") {
      const withPack = productVariations.find(
        (v) => v?.pack_price != null && v?.pack_qty != null
      );
      return withPack?.id ?? null;
    }

    const inStock = productVariations.find(
      (v) => v?.size_id !== null && v?.stock_qty !== 0
    );
    const withSize = productVariations.find((v) => v?.size_id !== null);
    return (inStock || withSize || productVariations[0])?.id ?? null;
  }, [productVariations]);

  const handleAddToCart = async (product) => {
    const hasSizes    = productVariations.some((v) => v.size_id !== null);
    const hasPackSizes = productVariations.some((v) => v.pack_price != null && v.pack_qty != null);
    const needsSelection = (UserRole.userRole !== 'distributor' && hasSizes) ||
                           (UserRole.userRole === 'distributor' && hasPackSizes);

    if (needsSelection && !selectedSize) {
      setSizeError(true);
      setTimeout(() => setSizeError(false), 3500);
      return false;
    }

    const resolvedSize = selectedSize;

    const selectedVariation = productVariations.find((v) => v.id === resolvedSize);

    let qtyToAdd =
      qtyRef.current !== null && !Number.isNaN(qtyRef.current)
        ? qtyRef.current
        : Number(selectedQty);

    if (UserRole.userRole === "distributor" && (!qtyToAdd || Number.isNaN(qtyToAdd))) {
      qtyToAdd = Number(selectedVariation?.pack_qty) || 1;
    }

    const success = await addToCart(product, resolvedSize, qtyToAdd);

    if (success) {
      setAddedProductName(`${product.prod_name} added to your cart!`);
      setAddSuccess(true);
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 }
      });
    }

    return success;
  };

  const mobileSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: false,
  };

  const images = product
    ? [
      product.primary_img,
      product.secondary_img,
      ...(product.product_image_list || []).map((imgObj) => imgObj.prod_img_url),
    ]
    : [];

  const hasVariationPack =
    UserRole.userRole === "distributor" &&
    productVariations.some((v) => v?.pack_price != null && v?.pack_qty != null);

  const hasBasePack =
    UserRole.userRole === "distributor" &&
    product?.pack_price != null &&
    product?.pack_qty != null;

  const activePackQty = (() => {
    if (!hasVariationPack && !hasBasePack) return 1;
    const activeVariation = selectedSize
      ? productVariations.find((v) => v.id === selectedSize)
      : productVariations.find((v) => v?.pack_qty != null);
    const baseQty = Number(activeVariation?.pack_qty || product?.pack_qty || 1);
    return baseQty > 0 ? baseQty : 1;
  })();

  const quantityOptions = Array.from(
    { length: 10 },
    (_, i) => (i + 1) * activePackQty
  );

  const getActivePackData = () => {
    if (UserRole.userRole === 'distributor') {
      const resolvedSize = selectedSize || getDefaultVariationId();
      return productVariations.find(v => v.id === resolvedSize) || product || null;
    }
    return product;
  };

  const handleQtyChange = (e) => {
    const qty = Number(e.target.value);
    qtyRef.current = qty;
    setSelectedQty(qty);

    const activePack = getActivePackData();
    if (!activePack) return;

    const pricePerItem = activePack.pack_qty ? activePack.pack_price / activePack.pack_qty : 0;
    setTotalPrice(pricePerItem * qty);
  };



  useEffect(() => {
    if (!product) return;

    const activePack = getActivePackData();

    if (activePack?.pack_qty && activePack?.pack_price) {
      const defaultQty = Number(activePack.pack_qty);

      setSelectedQty(defaultQty);
      setTotalPrice(activePack.pack_price);
      qtyRef.current = defaultQty;
    }
  }, [selectedSize, productVariations, product]);


  const sectionCardClass = "rounded-2xl border border-gray-100 bg-white p-4 md:p-5 shadow-sm mt-4";

  return (
    <>

      {product?.seo_metadata ? (
        <MetaData
          title={(product.seo_metadata?.meta_title !== "" ? product.seo_metadata?.meta_title : `${product?.prod_name} | MPI`) || MetaContentConfig.default.title}
          description={(product.seo_metadata?.meta_description !== '' ? product.seo_metadata?.meta_description : product?.prod_desc) || MetaContentConfig.default.description}
          keywords={(product.seo_metadata?.meta_keywords !== '' ? product.seo_metadata?.meta_keywords : product?.product_tag) || MetaContentConfig.default.keywords}
          image={(product.seo_metadata?.og_image !== '' ? product.seo_metadata?.og_image : product?.primary_img) || MetaContentConfig.default.image}
          url={(product.seo_metadata?.og_url !== '' ? product.seo_metadata?.og_url : `https://mpivirginplasticsample.com/p/${product?.slug}`) || MetaContentConfig.default.url}
          canonicalUrl={product.seo_metadata?.canonical_url}
          ogTitle={product.seo_metadata?.og_title}
          ogDescription={product.seo_metadata?.og_description}
          ogType={product.seo_metadata?.og_type}
        />
      ) : (
        <MetaData
          title={`${product?.prod_name} | MPI`}
          description={product?.prod_desc}
          keywords={product?.product_tag}
          image={product?.primary_img}
          url={`https://mpivirginplasticsample.com/p/${product?.slug}`}
        />
      )}

      {product && (
        <div className="hidden md:block max-w-7xl mx-auto px-8 pt-4">
          <Breadcrumb
            category="Products"
            productName={product.prod_name}
          />
        </div>
      )}

      {sizeChart && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-2xl shadow-xl p-4 max-w-md w-full">
            <div className="flex justify-end mb-2">
              <button onClick={() => setSizeChart(false)} className="w-8 h-8 flex items-center justify-center rounded-full text-gray-400 hover:bg-gray-100">
                <FiX size={16} />
              </button>
            </div>
            <img alt='chart-img' loading="lazy" className='w-full rounded-xl' src={product.size_chart_details?.chart_image} />
          </div>
        </div>
      )}

      <div className="single-product-page bg-gray-50">
        <div className="max-w-7xl mx-auto px-0 md:px-8 pb-12">
          <div className="flex flex-col sm:flex-row items-start gap-6 pt-0 md:pt-4 h-auto md:h-[700px]">
            {(!product) ? (
              <div className="hidden md:flex sm:flex-col gap-3 sm:w-1/12 w-full h-[700px] overflow-y-auto scrollbar-none">
                {[...Array(4)].map((_, i) => (
                  <Skeleton key={i} height={112} width={96} borderRadius={12} />
                ))}
              </div>
            ) : (
              <div className="md:flex sm:flex-col gap-3 sm:w-1/12 w-full hidden h-[700px] overflow-y-auto scrollbar-none">
                {images.map((img, index) => (
                  <LazyLoadImage
                    key={index}
                    src={compressImage(img, 200, 50, 'webp')}
                    alt={`${product.prod_name} thumbnail ${index + 1}`}
                    effect="blur"
                    className={`w-24 h-28 object-cover rounded-xl cursor-pointer border-2 transition-all ${selectedImage === img ? "border-brand-600" : "border-transparent hover:border-gray-200"
                      }`}
                    onClick={() => setSelectedImage(img)}
                  />
                ))}
              </div>
            )}

            {/* single */}
            {(!product) ? (
              <div className="w-full sm:w-1/2 md:block hidden h-[700px] rounded-2xl overflow-hidden border border-gray-100 bg-white">
                <Skeleton height="100%" width="100%" borderRadius={0} />
              </div>
            ) : (
              <div className="w-full sm:w-1/2 md:block hidden h-[700px] overflow-hidden rounded-2xl bg-white border border-gray-100 shadow-sm">
                <Zoom>
                  <img
                    src={selectedImage}
                    alt={product.prod_name}
                    style={{
                      cursor: "zoom-in",
                      height: '700px',
                      width: '100%',
                      objectFit: 'cover'
                    }}
                  />
                </Zoom>
              </div>
            )}

            {/* Mobile Slider */}
            {(!product) ? (
              <div className="block sm:hidden w-full h-[500px]">
                <Skeleton height="100%" width="100%" borderRadius={0} />
              </div>
            ) : (
              <div className="block sm:hidden w-full">
                <Slider {...mobileSettings}>
                  {images.map((img, index) => (
                    <div key={index}>
                      <img
                        src={img}
                        alt={`${product.prod_name} thumbnail ${index + 1}`}
                        className="w-full h-[500px] object-cover object-top"
                      />
                    </div>
                  ))}
                </Slider>
              </div>
            )}

            {/* Product Information */}
            {
              (!product) ? (
                <div className="w-full sm:w-1/2 flex flex-col h-auto px-4 md:px-0">
                  <div className="rounded-2xl border border-gray-100 bg-white p-4 md:p-5 shadow-sm space-y-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="space-y-2 flex-1">
                        <Skeleton height={10} width={80} />
                        <Skeleton height={20} width="70%" />
                      </div>
                      <Skeleton height={28} width={60} borderRadius={8} />
                    </div>
                    <div className="flex items-end gap-2">
                      <Skeleton height={26} width={70} />
                      <Skeleton height={18} width={60} />
                      <Skeleton height={16} width={50} />
                    </div>
                    <div className="space-y-2">
                      <Skeleton height={10} width={50} />
                      <div className="flex gap-2">
                        {[...Array(4)].map((_, i) => <Skeleton key={i} height={36} width={36} circle />)}
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Skeleton height={10} width={40} />
                      <div className="flex gap-2">
                        {[...Array(4)].map((_, i) => <Skeleton key={i} height={38} width={52} borderRadius={8} />)}
                      </div>
                    </div>
                    <div className="flex gap-3 pt-2">
                      <Skeleton height={48} width="50%" borderRadius={12} />
                      <Skeleton height={48} width="50%" borderRadius={12} />
                    </div>
                  </div>
                </div>
              ) : (
                <div className="w-full sm:w-1/2 flex flex-col h-auto md:h-[750px] md:overflow-y-auto overflow-visible scrollbar-none px-4 md:px-0">
                  <div className="rounded-2xl border border-gray-100 bg-white p-4 md:p-5 shadow-sm">
                    <div className='flex items-start justify-between gap-3'>
                      <div>
                        <h2 className="text-gray-400 font-bold text-[11px] md:text-xs uppercase tracking-wide">{product.brand_details?.brand_name}</h2>
                        <p className="text-gray-900 text-base md:text-lg font-bold mt-0.5">{product.prod_name}</p>
                      </div>
                      {productReviews && productReviews.length > 0 && (
                        <p className='flex items-center gap-1 text-brand-600 font-bold text-sm bg-brand-600/10 px-2.5 py-1 rounded-lg whitespace-nowrap'>
                          <FiStar size={13} className="fill-current" /> {overallRating}
                          <span className="text-gray-400 font-medium">({productReviews.length})</span>
                        </p>
                      )}
                    </div>

                    {UserRole.userRole !== 'distributor' && (
                      !selectedSize ? (
                        <div className="flex items-center gap-3 mt-3 flex-wrap">
                          <p className="font-bold text-xl text-gray-900">
                            ₹{product.sale_price || product.regular_price}
                          </p>
                          {product.sale_price && (
                            <p className="text-sm text-gray-400 line-through">₹{product.regular_price}</p>
                          )}
                          {product.regular_price > product.sale_price && (
                            <p className='text-green-600 font-bold text-sm'>
                              {(100 - (product.sale_price / product.regular_price) * 100).toFixed(0)}% OFF
                            </p>
                          )}
                        </div>
                      ) : (
                        <>
                          {Array.isArray(productVariations) && productVariations.length > 0 ? (
                            productVariations
                              .filter((size) => size.id === selectedSize)
                              .map((size) => (
                                <div key={size.size_id} className="flex items-center gap-3 mt-3 flex-wrap">
                                  <p className="font-bold text-xl text-gray-900">
                                    ₹{size.sale_price || size.regular_price}
                                  </p>
                                  {size.sale_price && (
                                    <p className="text-sm text-gray-400 line-through">₹{size.regular_price}</p>
                                  )}
                                  {size.regular_price > size.sale_price && (
                                    <p className='text-green-600 font-bold text-sm'>
                                      {(100 - (size.sale_price / size.regular_price) * 100).toFixed(0)}% OFF
                                    </p>
                                  )}
                                </div>
                              ))
                          ) : null}
                        </>
                      ))}

                    {UserRole.userRole === 'distributor' && (hasVariationPack || hasBasePack) && (
                      <div className="flex flex-col mt-3 gap-2.5">
                        <p className="font-bold text-xl text-gray-900">
                          Total Price: ₹{totalPrice}
                        </p>

                        <div className="flex items-center gap-3">
                          <span className="text-sm font-semibold text-gray-600">
                            Quantity
                          </span>
                          <select
                            disabled={productVariations.length > 0 && !selectedSize}
                            value={selectedQty || ""}
                            onChange={handleQtyChange}
                            className="border border-gray-200 rounded-xl px-3 py-2 text-sm
                              disabled:bg-gray-100 disabled:cursor-not-allowed
                              focus:outline-none focus:ring-2 focus:ring-brand-600/30 focus:border-brand-600
                              bg-white cursor-pointer shadow-sm"
                          >
                            {quantityOptions.map((qty) => (
                              <option key={qty} value={qty}>
                                {qty} items
                              </option>
                            ))}
                          </select>
                        </div>
                        {productVariations.length > 0 && !selectedSize && (
                          <p className="text-amber-600 text-xs font-medium">
                            Select a pack size below to choose your quantity.
                          </p>
                        )}
                      </div>
                    )}

                    <p className='text-gray-400 text-xs mt-2'>Inclusive of all taxes</p>

                    {(product.product_tag || product.product_quality) && (
                      <div className='flex gap-2 mt-3 flex-wrap'>
                        {(product.product_tag && product.product_tag !== 'null') && (
                          <span className='px-2.5 py-1 rounded-lg bg-green-100 text-green-700 font-bold uppercase text-[10px] md:text-xs'>
                            {product.product_tag}
                          </span>
                        )}
                        {(product.product_quality && product.product_quality !== 'null') && (
                          <span className='px-2.5 py-1 rounded-lg bg-gray-100 text-gray-600 font-bold uppercase text-[10px] md:text-xs'>
                            {product.product_quality}
                          </span>
                        )}
                      </div>
                    )}

                    {/* Color Selection */}
                    {colorVariations.id ? (
                      <div key={colorVariations.id} className="mt-4 pt-4 border-t border-gray-100">
                        <p className="font-semibold text-sm text-gray-700 mb-2.5">
                          Color: <span className="text-gray-500 font-normal">{colorVariations.color_name}</span>
                        </p>
                        <div className="flex items-center gap-2.5">
                          <div
                            style={{ border: `2px solid ${colorVariations.color_code}` }}
                            className="p-1 rounded-full inline-block"
                          >
                            <div
                              className="w-7 h-7 rounded-full"
                              style={{ backgroundColor: colorVariations.color_code }}
                            ></div>
                          </div>

                          {productVariations
                            .filter((variation) => variation.color !== null && variation.color_page_link !== "null")
                            .map((variation) => (
                              <React.Fragment key={variation.color_id}>
                                <button
                                  className="w-7 h-7 rounded-full border-2 border-gray-200 hover:border-gray-300 inline-block transition-colors"
                                  onClick={() => (window.location.href = variation.color_page_link)}
                                  style={{ backgroundColor: variation.color }}
                                ></button>
                              </React.Fragment>
                            ))}
                        </div>
                      </div>
                    ) : (
                      productVariations.some((variation) => variation.color) && (
                        <div className="mt-4 pt-4 border-t border-gray-100">
                          <p className="font-semibold text-sm text-gray-700 mb-2.5">Select Color</p>
                          <div className="flex items-center gap-2.5">
                            {productVariations
                              .filter((variation) => variation.color !== null && variation.color_page_link !== "null")
                              .map((variation) => (
                                <React.Fragment key={variation.color_id}>
                                  <Link
                                    className="w-7 h-7 rounded-full border-2 border-gray-200 hover:border-gray-300 inline-block transition-colors"
                                    to={variation.color_page_link}
                                    style={{ backgroundColor: variation.color }}
                                  ></Link>
                                </React.Fragment>
                              ))}
                          </div>
                        </div>
                      )
                    )}

                    {/* Size Selection */}
                    {UserRole.userRole !== 'distributor' && productVariations.some(v => v.size_id !== null) && (
                      <div className="mt-4 pt-4 border-t border-gray-100">
                        <div className="flex justify-between items-center">
                          <p className="font-semibold text-sm text-gray-700">
                            Select Size
                          </p>

                          {product.size_chart_details && (
                            <button
                              onClick={() => setSizeChart(true)}
                              className="text-brand-600 text-xs font-bold hover:underline"
                            >
                              SIZE GUIDE
                            </button>
                          )}
                        </div>

                        {sizeError && (
                          <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-600 text-xs rounded-xl px-3 py-2 mt-2.5">
                            <span>⚠️</span>
                            <span className="font-semibold">Please select a size to continue.</span>
                          </div>
                        )}

                        <div className={`mt-2.5 flex gap-2 flex-wrap ${sizeError ? 'ring-2 ring-red-200 rounded-xl p-2' : ''}`}>
                          {productVariations
                            .filter(size => size.size !== null)
                            .map(size => {
                              const isSelected = selectedSize === size.id;
                              const isOut = size.stock_qty === 0;
                              const isLow = size.stock_qty < 5 && size.stock_qty !== 0;

                              return (
                                <div key={size.size_id}>
                                  <label
                                    className={`relative h-10 min-w-[2.5rem] px-3 rounded-xl flex items-center justify-center border font-semibold text-sm transition-all
                                      ${isSelected ? 'bg-brand-600 text-white border-brand-600' : 'bg-gray-50 border-gray-200 hover:border-gray-300'}
                                      ${isOut ? 'text-gray-300 border-gray-100 cursor-default bg-gray-50' : 'cursor-pointer'}
                                      ${isLow && !isSelected ? 'text-red-500 border-red-300' : ''}
                                    `}
                                  >
                                    <input
                                      type="radio"
                                      name="size"
                                      checked={isSelected}
                                      onChange={() => { setSelectedSize(size.id); setSizeError(false); }}
                                      disabled={isOut}
                                      className="absolute opacity-0 -z-10"
                                    />
                                    {size.size}
                                  </label>

                                  {isLow && (
                                    <p className="text-red-500 text-[10px] mt-1 text-center">{size.stock_qty} Left</p>
                                  )}
                                </div>
                              );
                            })}
                        </div>
                      </div>
                    )}

                    {UserRole.userRole === 'distributor' && hasVariationPack && (
                      <div className="mt-4 pt-4 border-t border-gray-100">
                        <p className="font-semibold text-sm text-gray-700">
                          Select Pack Size
                        </p>

                        {sizeError && (
                          <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-600 text-xs rounded-xl px-3 py-2 mt-2.5">
                            <span>⚠️</span>
                            <span className="font-semibold">Please select a pack size to continue.</span>
                          </div>
                        )}

                        <div className={`mt-2.5 flex gap-2 flex-wrap ${sizeError ? 'ring-2 ring-red-200 rounded-xl p-2' : ''}`}>
                          {productVariations
                            .filter(size => size.pack_price != null && size.pack_qty != null)
                            .map(size => {
                              const isSelected = selectedSize === size.id;

                              return (
                                <label
                                  key={size.size_id}
                                  className={`h-10 px-3.5 rounded-xl flex items-center justify-center border font-semibold text-sm cursor-pointer transition-all
                                    ${isSelected ? 'bg-brand-600 text-white border-brand-600' : 'bg-gray-50 border-gray-200 hover:border-gray-300'}
                                  `}
                                >
                                  <input
                                    type="radio"
                                    name="size"
                                    checked={isSelected}
                                    onChange={() => { setSelectedSize(size.id); setSizeError(false); }}
                                    className="absolute opacity-0 -z-10"
                                  />
                                  {size.size}
                                </label>
                              );
                            })}
                        </div>
                      </div>
                    )}

                    {/* Add to Cart and Wishlist Button */}
                    <div className="flex items-center flex-wrap gap-2 mt-5 pt-4 border-t border-gray-100">
                      <button
                        disabled={addToCartLoading}
                        onClick={async () => {
                          if (addSuccess) {
                            navigate('/cart');
                            return;
                          }
                          setAddToCartLoading(true);
                          try {
                            await handleAddToCart(product);
                          } finally {
                            setAddToCartLoading(false);
                          }
                        }}
                        className={`text-white px-6 gap-2 py-3 rounded-xl flex items-center text-sm font-semibold transition-colors disabled:opacity-60
                          ${addSuccess ? 'bg-[#2CA003] hover:bg-[#248a02] flex-col w-full' : 'bg-brand-600 hover:bg-brand-700'}`}
                      >
                        {addSuccess && addedProductName}
                        {addSuccess && (<hr className='h-1 text-white w-full border-white/30' />)}
                        {!addSuccess && (
                          addToCartLoading ? (
                            <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                          ) : (
                            <FaCartPlus />
                          )
                        )}
                        <div className='flex gap-2 items-center'>
                          <span className={`hidden md:block`}>
                            {addSuccess ? 'Go to Bag' : addToCartLoading ? 'Adding...' : 'Add to Cart'}
                          </span>
                          {addSuccess && (
                            <svg width="17" height="16" viewBox="0 0 17 16" fill="none">
                              <path d="M9.5 3L14.5 8M14.5 8L9.5 13M14.5 8H2.5" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          )}
                        </div>
                      </button>

                      <button
                        disabled={buyNowLoading}
                        onClick={async () => {
                          setBuyNowLoading(true);
                          try {
                            const success = await handleAddToCart(product);
                            if (success) {
                              setTimeout(() => {
                                navigate('/checkout');
                              }, 800)
                            }
                          } finally {
                            setBuyNowLoading(false);
                          }
                        }}
                        className="text-white bg-brand-600 hover:bg-brand-700 px-6 gap-2 py-3 rounded-xl flex items-center text-sm font-semibold transition-all hover:scale-[1.02] hover:shadow-md disabled:opacity-60 disabled:hover:scale-100"
                      >
                        {buyNowLoading ? (
                          <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                        ) : (
                          <FaBolt className="text-yellow-400" />
                        )}
                        <span>{buyNowLoading ? 'Please wait...' : 'BUY NOW'}</span>
                      </button>

                      <WishlistButton
                        product={product}
                        wishlist={wishlist}
                        setWishlist={setWishlist}
                        token={token}
                        content
                      />
                    </div>
                  </div>

                  <div className={sectionCardClass}>
                    <DeliveryEstimate />
                  </div>

                  <div className={sectionCardClass}>
                    <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
                      <div className="flex items-center gap-1.5">
                        {Number(product.is_returnable) === 1 ? (
                          <FiCheckCircle className="text-brand-600" />
                        ) : (
                          <FiXCircle className="text-gray-400" />
                        )}
                        <span className={Number(product.is_returnable) === 1 ? 'text-gray-700 font-medium' : 'text-gray-400'}>
                          {Number(product.is_returnable) === 1 ? 'Returnable' : 'Not Returnable'}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        {Number(product.is_cod) === 1 ? (
                          <FiCheckCircle className="text-brand-600" />
                        ) : (
                          <FiXCircle className="text-gray-400" />
                        )}
                        <span className={Number(product.is_cod) === 1 ? 'text-gray-700 font-medium' : 'text-gray-400'}>
                          {Number(product.is_cod) === 1 ? 'Cash on Delivery Available' : 'Cash on Delivery Not Available'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Product Highlights */}
                  {keyHighlights.length > 0 ? (
                    <div className={sectionCardClass}>
                      <h2 className="font-bold text-base text-gray-900 mb-3">Key Highlights</h2>
                      <div className='flex gap-x-4 gap-y-3 flex-wrap'>
                        {keyHighlights.map((highlight, index) => (
                          <div key={highlight.id || index} className="flex flex-col gap-0.5 w-[47%]">
                            <p className="text-gray-400 text-xs font-medium">{highlight.label}</p>
                            <p className="text-gray-800 font-semibold text-sm">{highlight.value}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : null}

                  <div className={sectionCardClass}>
                    <ProductDetailsAccordian productdetails={product.prod_desc} />
                  </div>

                  <div className={sectionCardClass}>
                    <ReviewRatingComments
                      reviews={productReviews}
                      ratingBreakdown={ratingBreakdown}
                      totalRatings={productReviews.length}
                      overallRating={overallRating}
                    />
                  </div>

                  {gmbReviews.length > 0 && (
                    <div className={sectionCardClass}>
                      <GmbReviewsSection reviews={gmbReviews} />
                    </div>
                  )}
                </div>
              )}
          </div>
          <div className='my-4 remove_nav ms-2 md:ms-0'>
            {product && <YouAlsoLike currentProduct={product} />}
          </div>

          <div className='my-4 remove_nav ms-2 md:ms-0'>
            {!loading && (
              <RecentlyViewed />
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default ProductDetailsAll;
